import React, { useEffect, useMemo, useState } from "react";
import { Icons } from "../../components/common/Icons";
import { useEmployeeData } from "../../context/EmployeeContext";
import AvatarUpload from "../../components/common/AvatarUpload";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import EmpEIDCard from "./EmpEIDCard";
import "../../assets/styles/Employee/EmpProfileSection.css";

const MAX_SKILLS = 16;
const SUGGESTED_SKILLS = [
  "Data Analysis",
  "React",
  "Node.js",
  "SQL",
  "Communication",
  "Leadership",
  "Project Management",
  "Excel"
];

const parseSkills = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v || "").trim()).filter(Boolean);
  if (typeof value === "string") return value.split(",").map((v) => v.trim()).filter(Boolean);
  return [];
};

const EDITABLE_PROFILE_FIELDS = {
  phone: ["phone", "mobile", "personal phone"],
  personal_email: ["personal email", "alternate email", "other email"],
  emergency_contact: ["emergency contact", "emergency number"],
  current_address: ["current address"],
  current_city: ["current city"],
  current_state: ["current state"],
  current_pincode: ["current pincode", "current postal code"],
  permanent_address: ["permanent address"],
  permanent_city: ["permanent city"],
  permanent_state: ["permanent state"],
  permanent_pincode: ["permanent pincode", "permanent postal code"],
  location: ["location"]
};

const editableFieldFromLabel = (label) => {
  const key = String(label || "").trim().toLowerCase();
  for (const [field, aliases] of Object.entries(EDITABLE_PROFILE_FIELDS)) {
    if (aliases.includes(key)) return field;
  }
  return null;
};

function EmpProfileSection() {
  const { empProfileDetails, empUploadedDocs, empInfo, refreshEmployeeData } = useEmployeeData();
  const { user, refreshUser } = useAuth();
  const [policyDocs, setPolicyDocs] = useState([]);
  const [profileDraft, setProfileDraft] = useState({
    phone: "",
    personal_email: "",
    emergency_contact: "",
    current_address: "",
    current_city: "",
    current_state: "",
    current_pincode: "",
    permanent_address: "",
    permanent_city: "",
    permanent_state: "",
    permanent_pincode: "",
    location: ""
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [activeInlineField, setActiveInlineField] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [skillsDraft, setSkillsDraft] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState("");
  const [savingSkills, setSavingSkills] = useState(false);
  const [skillsMsg, setSkillsMsg] = useState("");
  const [showEID, setShowEID] = useState(false);
  const [avatarKey, setAvatarKey] = useState(0);
  const [profileKey, setProfileKey] = useState(0); // bumped after any profile save → re-fetches EID card

  useEffect(() => {
    let alive = true;
    api.getPolicies()
      .then((rows) => {
        if (!alive) return;
        const docs = (Array.isArray(rows) ? rows : [])
          .filter((p) => !!p?.file_path)
          .map((p) => ({
            id: p.id,
            name: p.name || p.title || "Policy",
            date: p.updated || "",
            category: p.category || "General",
            kind: "policy"
          }));
        setPolicyDocs(docs);
      })
      .catch(() => setPolicyDocs([]));
    return () => { alive = false; };
  }, []);

  const fillProfileDraft = (profile) => {
    const p = profile || {};
    setProfileDraft({
      phone: p.personal_phone || p.phone || "",
      personal_email: p.personal_email || p.email || "",
      emergency_contact: p.emergency_contact || "",
      current_address: p.current_address || p.address || "",
      current_city: p.current_city || p.city || "",
      current_state: p.current_state || p.state || "",
      current_pincode: p.current_pincode || p.pincode || "",
      permanent_address: p.permanent_address || "",
      permanent_city: p.permanent_city || "",
      permanent_state: p.permanent_state || "",
      permanent_pincode: p.permanent_pincode || "",
      location: p.location || ""
    });
  };

  useEffect(() => {
    let alive = true;
    api.getProfile()
      .then((data) => {
        if (!alive) return;
        const profile = data?.profile || data || {};
        setSkillsDraft(parseSkills(profile.skills));
        fillProfileDraft(profile);
      })
      .catch(() => {
        if (!alive) return;
        setSkillsDraft([]);
      });
    return () => { alive = false; };
  }, []);

  const updatedDocs = useMemo(() => {
    const userDocs = (empUploadedDocs || []).map((d) => ({ ...d, kind: "user" }));
    return [...policyDocs, ...userDocs];
  }, [empUploadedDocs, policyDocs]);

  const handleDownload = async (doc) => {
    try {
      if (doc.kind === "policy") {
        const res = await api.downloadPolicy(doc.id);
        const filename = res?.name || doc.name || "policy";
        if (res?.url) {
          await api.downloadFile(res.url, filename);
          return;
        }
        if (res?.content) {
          const blob = new Blob([res.content], { type: "text/plain" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename.endsWith(".txt") ? filename : `${filename}.txt`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          return;
        }
      }

      if (doc.id) {
        await api.downloadDocById(doc.id, doc.name || "document");
        return;
      }
      alert("Document download not available");
    } catch (e) {
      alert("Download failed: " + e.message);
    }
  };

  const addSkillToDraft = (raw) => {
    const incoming = parseSkills(String(raw || "").replace(/\n/g, ","));
    if (!incoming.length) return;
    setSkillsDraft((prev) => {
      const unique = Array.from(new Set([...prev, ...incoming]));
      return unique.slice(0, MAX_SKILLS);
    });
  };

  const removeSkill = (index) => {
    setSkillsDraft((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(-1);
      setEditingValue("");
    }
  };

  const saveEditedSkill = () => {
    const value = String(editingValue || "").trim();
    if (editingIndex < 0 || !value) return;
    setSkillsDraft((prev) => {
      const next = [...prev];
      next[editingIndex] = value;
      return Array.from(new Set(next));
    });
    setEditingIndex(-1);
    setEditingValue("");
  };

  const handleSaveSkills = async () => {
    setSavingSkills(true);
    setSkillsMsg("");
    try {
      await api.updateProfile({ skills: skillsDraft });
      await refreshEmployeeData?.();
      setSkillsMsg("Skills updated");
    } catch (e) {
      setSkillsMsg(`Save failed: ${e.message}`);
    } finally {
      setSavingSkills(false);
    }
  };

  const handleProfileChange = (key, value) => {
    setProfileDraft((prev) => ({ ...prev, [key]: value }));
  };

  const validateProfileDraft = () => {
    const phone = String(profileDraft.phone || "").trim();
    const emergency = String(profileDraft.emergency_contact || "").trim();
    const email = String(profileDraft.personal_email || "").trim();
    const currentPin = String(profileDraft.current_pincode || "").trim();
    const permanentPin = String(profileDraft.permanent_pincode || "").trim();
    if (phone && !/^\d{10}$/.test(phone)) return "Phone must be 10 digits";
    if (emergency && !/^\d{10}$/.test(emergency)) return "Emergency contact must be 10 digits";
    if (email && !/^\S+@\S+\.\S+$/.test(email)) return "Enter valid email";
    if (currentPin && !/^\d{6}$/.test(currentPin)) return "Current pincode must be 6 digits";
    if (permanentPin && !/^\d{6}$/.test(permanentPin)) return "Permanent pincode must be 6 digits";
    return "";
  };

  const handleSaveProfile = async () => {
    const err = validateProfileDraft();
    if (err) {
      setProfileMsg(err);
      return;
    }
    setSavingProfile(true);
    setProfileMsg("");
    try {
      await api.updateProfile({
        phone: profileDraft.phone || null,
        personal_email: profileDraft.personal_email || null,
        emergency_contact: profileDraft.emergency_contact || null,
        current_address: profileDraft.current_address || null,
        current_city: profileDraft.current_city || null,
        current_state: profileDraft.current_state || null,
        current_pincode: profileDraft.current_pincode || null,
        permanent_address: profileDraft.permanent_address || null,
        permanent_city: profileDraft.permanent_city || null,
        permanent_state: profileDraft.permanent_state || null,
        permanent_pincode: profileDraft.permanent_pincode || null,
        location: profileDraft.location || null
      });
      await refreshEmployeeData?.();
      setIsEditingProfile(false);
      setActiveInlineField("");
      setProfileMsg("Profile updated");
      setProfileKey(k => k + 1); // re-fetch EID card with new emergency contact
    } catch (e) {
      setProfileMsg(`Save failed: ${e.message}`);
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <>
    {/* E-ID Card - collapsible section */}
    <div className="card animate-in mb-24" style={{marginBottom:24}}>
      <div
        onClick={() => setShowEID(v => !v)}
        style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"16px 20px", cursor:"pointer", userSelect:"none",
          borderRadius: showEID ? "12px 12px 0 0" : 12,
          background: showEID
            ? "linear-gradient(135deg,#0a2244,#1a5fa8)"
            : "linear-gradient(135deg,#f0f7ff,#e8f0fe)",
          transition:"all .25s",
        }}
      >
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:22}}>🪪</span>
          <div>
            <div style={{fontSize:14,fontWeight:800,color: showEID ? "#fff" : "#0a2244"}}>E-ID Card</div>
            <div style={{fontSize:11,color: showEID ? "rgba(255,255,255,.65)" : "#64748b",marginTop:1}}>
              {showEID ? "Click to collapse" : "Click to view your identity card"}
            </div>
          </div>
        </div>
        <span style={{fontSize:18,color: showEID ? "#fff" : "#1a5fa8",transition:"transform .25s",display:"inline-block",transform: showEID ? "rotate(180deg)" : "rotate(0deg)"}}>
          ▼
        </span>
      </div>
      {showEID && (
        <div style={{padding:"16px 20px",borderTop:"1px solid #dbeafe"}}>
          <EmpEIDCard avatarKey={avatarKey + profileKey} />
        </div>
      )}
    </div>
    <div className="grid-2 mb-24">
      <div className="card animate-in delay-3">
        <div className="card-header"><div className="card-title">Profile Details</div></div>
        <div className="card-body">
          <div className="avatar-upload-wrapper">
  <AvatarUpload
    currentAvatar={user?.avatar}
    name={empInfo.name || user?.name}
    size={64}
    editable={true}
    onUploaded={() => { 
      refreshUser?.(); 
      setAvatarKey(k => k + 1); 
    }} 
  />
</div>
          <div className="emp-inline-note">HR-locked fields cannot be edited. Contact and address fields can be edited using the icon.</div>
          {empProfileDetails.map((p, i) => {
            const editableKey = editableFieldFromLabel(p.label);
            const isEditable = Boolean(editableKey);
            const rowValue = isEditable ? (profileDraft[editableKey] || "-") : (p.value || "-");
            const showInput = isEditable && isEditingProfile && activeInlineField === editableKey;
            return (
              <div className="profile-field" key={i}>
                <div className="profile-label">{p.label}</div>
                <div className="profile-value profile-value-inline-edit">
                  {showInput ? (
                    <input
                      className="fi emp-inline-input"
                      type={["phone", "emergency_contact"].includes(editableKey) ? "tel" : "text"}
                      maxLength={["phone", "emergency_contact"].includes(editableKey) ? 10 : undefined}
                      placeholder={editableKey === "emergency_contact" ? "10-digit mobile number" : undefined}
                      value={profileDraft[editableKey] || ""}
                      autoFocus
                      onChange={(e) => handleProfileChange(editableKey, e.target.value)}
                    />
                  ) : (
                    <span>{rowValue}</span>
                  )}
                  {isEditable ? (
                    <button
                      type="button"
                      className="emp-inline-edit-btn"
                      onClick={() => {
                        setProfileMsg("");
                        setIsEditingProfile(true);
                        setActiveInlineField(editableKey);
                      }}
                      title={`Edit ${p.label}`}
                      aria-label={`Edit ${p.label}`}
                    >
                      <Icons.Edit />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
          {isEditingProfile ? (
            <div className="emp-inline-actions">
              <button className="btn-o btn-s" type="button" onClick={handleSaveProfile} disabled={savingProfile}>{savingProfile ? "Saving..." : "Save Changes"}</button>
              <button className="btn-o btn-s" type="button" onClick={async () => {
                setIsEditingProfile(false);
                setActiveInlineField("");
                setProfileMsg("");
                try {
                  const data = await api.getProfile();
                  fillProfileDraft(data?.profile || {});
                } catch (_) {}
              }}>Cancel</button>
              {profileMsg ? <span style={{ fontSize: 12, color: "#475569" }}>{profileMsg}</span> : null}
            </div>
          ) : (
            profileMsg ? <div className="emp-inline-actions"><span style={{ fontSize: 12, color: "#475569" }}>{profileMsg}</span></div> : null
          )}
          <div className="profile-field">
            <div className="profile-label">Skills (Editable)</div>
            <div className="emp-skill-box">
              <div className="emp-skill-add-row">
                <input
                  className="fi"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder="Type a skill and click Add"
                />
                <button
                  className="btn-o btn-s"
                  type="button"
                  onClick={() => {
                    addSkillToDraft(skillInput);
                    setSkillInput("");
                  }}
                  disabled={!skillInput.trim() || skillsDraft.length >= MAX_SKILLS}
                >
                  Add Skill
                </button>
              </div>

              <div className="emp-skill-hint">
                You can add up to {MAX_SKILLS} skills ({Math.max(0, MAX_SKILLS - skillsDraft.length)} left)
              </div>

              <div className="emp-suggested-row">
                {SUGGESTED_SKILLS.map((skill) => {
                  const exists = skillsDraft.some((s) => s.toLowerCase() === skill.toLowerCase());
                  return (
                    <button
                      key={skill}
                      type="button"
                      className="emp-suggested-chip"
                      onClick={() => addSkillToDraft(skill)}
                      disabled={exists || skillsDraft.length >= MAX_SKILLS}
                    >
                      {skill} +
                    </button>
                  );
                })}
              </div>

              <div className="emp-skill-list">
                {skillsDraft.length === 0 ? (
                  <div className="emp-skill-empty">No skills added yet</div>
                ) : (
                  skillsDraft.map((skill, i) => (
                    <div className="emp-skill-item" key={`${skill}-${i}`}>
                      {editingIndex === i ? (
                        <>
                          <input
                            className="fi emp-skill-edit-input"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                          />
                          <button className="btn-o btn-s" type="button" onClick={saveEditedSkill}>Save</button>
                          <button className="btn-o btn-s" type="button" onClick={() => { setEditingIndex(-1); setEditingValue(""); }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <span className="emp-skill-item-text">{skill}</span>
                          <div className="emp-skill-item-actions">
                            <button className="btn-o btn-s" type="button" onClick={() => { setEditingIndex(i); setEditingValue(skill); }}>Edit</button>
                            <button className="btn-o btn-s" type="button" onClick={() => removeSkill(i)}>Remove</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="btn-o btn-s" onClick={handleSaveSkills} disabled={savingSkills}>
                  {savingSkills ? "Saving..." : "Save Skills"}
                </button>
                {skillsMsg ? <span style={{ fontSize: 12, color: "#475569" }}>{skillsMsg}</span> : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card animate-in delay-4">
        <div className="card-header"><div className="card-title">Updated Documents</div><div className="card-subtitle">{updatedDocs.length} documents</div></div>
        <div className="card-body">
          {updatedDocs.length === 0 ?
            <div className="empprofilesection-inline-5">No documents available yet</div> :
            updatedDocs.map((d, i) =>
              <div className="upload-doc" key={i}>
                <div>
                  <div className="upload-doc-name">{d.name}</div>
                  <div className="upload-doc-date">{d.kind === "policy" ? `Policy (${d.category || "General"})` : "Uploaded"}: {d.date ? new Date(d.date).toLocaleDateString("en-IN") : "-"}</div>
                </div>
                <div className="doc-download empprofilesection-inline-6" onClick={() => handleDownload(d)}><Icons.Download /> Download</div>
              </div>
            )}
        </div>
      </div>
    </div>
  </>
  );
}

export default EmpProfileSection;
